import { useState, useCallback, useMemo } from 'react';
import { ConfigStep, ConfigOption, SelectedOption } from '../types';

// 경로별 스텝 키 순서 정의
export const STEP_FLOWS: Record<string, string[]> = {
  'electric_single_cart': ['power_type', 'power_source', 'motor_origin', 'frame_type', 'pump', 'pump_option', 'hose'],
  'electric_single_mount': ['power_type', 'power_source', 'motor_origin', 'frame_type', 'pump', 'pump_option'],
  'electric_three_cart': ['power_type', 'power_source', 'motor_capacity', 'frame_type', 'pump', 'pump_option', 'hose'],
  'electric_three_mount': ['power_type', 'power_source', 'motor_capacity', 'frame_type', 'pump', 'pump_option'],
  'engine_cart': ['power_type', 'frame_type', 'engine_brand', 'engine_power', 'start_method', 'pump', 'pump_option', 'hose'],
  'engine_stand': ['power_type', 'frame_type', 'frame_sub_type', 'engine_brand', 'engine_power', 'start_method', 'pump', 'pump_option'],
};

// 선택 목록으로부터 경로(flow) 판정 - 순수 함수
export function detectFlowKey(newSelections: SelectedOption[]): string {
  const powerType = newSelections.find((s) => s.step.step_key === 'power_type');
  const powerSource = newSelections.find((s) => s.step.step_key === 'power_source');
  const frameType = newSelections.find((s) => s.step.step_key === 'frame_type');

  if (powerType?.option.option_name.includes('전기')) {
    const isMount = frameType?.option.option_name.includes('좌대');
    if (powerSource?.option.option_name.includes('삼상')) {
      return isMount ? 'electric_three_mount' : 'electric_three_cart';
    }
    return isMount ? 'electric_single_mount' : 'electric_single_cart';
  } else if (powerType?.option.option_name.includes('엔진')) {
    if (frameType?.option.option_name.includes('좌대')) {
      return 'engine_stand';
    }
    return 'engine_cart';
  }
  return 'electric_single_cart';
}

// 선택 목록이 해당 경로의 모든 단계를 채웠는지(구성 완료) 판정
export function isSelectionComplete(selections: SelectedOption[]): boolean {
  if (selections.length === 0) return false;
  const flow = STEP_FLOWS[detectFlowKey(selections)] || [];
  return flow.every((key) => selections.some((s) => s.step.step_key === key));
}

export function useConfigStore() {
  const [allSteps, setAllSteps] = useState<ConfigStep[]>([]);
  const [selections, setSelections] = useState<SelectedOption[]>([]);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [flowKey, setFlowKey] = useState<string>('electric_single_cart');

  const activeFlow = STEP_FLOWS[flowKey] || STEP_FLOWS['electric_single_cart'];

  // activeSteps는 매 렌더마다 새 배열로 생성되므로, 참조를 안정화하지 않으면
  // 이 값을 effect 의존성에 넣는 소비자에서 무한 렌더 루프가 발생한다.
  // (allSteps/flowKey가 바뀔 때만 새로 계산)
  const activeSteps = useMemo(
    () =>
      activeFlow
        .map((key) => allSteps.find((s) => s.step_key === key))
        .filter(Boolean) as ConfigStep[],
    [activeFlow, allSteps],
  );

  const currentStep = activeSteps[currentStepIndex] || null;

  // 가격은 최종 제품 base_price 단일가만 사용하므로 옵션 합산은 폐기 (호출부 호환용으로 0 유지)
  const totalPrice = 0;

  const detectFlow = useCallback((newSelections: SelectedOption[]) => detectFlowKey(newSelections), []);

  const selectOption = useCallback((step: ConfigStep, option: ConfigOption) => {
    setSelections((prev) => {
      // 현재 단계 이후 선택 초기화
      const currentKeyIndex = activeFlow.indexOf(step.step_key);
      const kept = prev.filter((s) => {
        const idx = activeFlow.indexOf(s.step.step_key);
        return idx < currentKeyIndex;
      });
      const updated = [...kept, { step, option }];

      const newFlow = detectFlow(updated);
      if (newFlow !== flowKey) {
        setFlowKey(newFlow);
      }

      return updated;
    });
  }, [activeFlow, flowKey, detectFlow]);

  const goNext = useCallback(() => {
    if (currentStepIndex < activeSteps.length - 1) {
      setCurrentStepIndex((prev) => prev + 1);
    }
  }, [currentStepIndex, activeSteps.length]);

  const goBack = useCallback(() => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex((prev) => prev - 1);
    }
  }, [currentStepIndex]);

  const goToStep = useCallback((index: number) => {
    if (index >= 0 && index < activeSteps.length) {
      setCurrentStepIndex(index);
    }
  }, [activeSteps.length]);

  const restoreSelections = useCallback((restored: SelectedOption[]) => {
    const newFlow = detectFlow(restored);
    setFlowKey(newFlow);
    setSelections(restored);
    const flow = STEP_FLOWS[newFlow] || STEP_FLOWS['electric_single_cart'];
    // currentStepIndex를 마지막 선택 단계의 다음으로 설정
    if (restored.length > 0) {
      const lastKey = restored[restored.length - 1].step.step_key;
      const lastIdx = flow.indexOf(lastKey);
      setCurrentStepIndex(Math.min(lastIdx + 1, flow.length - 1));
    } else {
      setCurrentStepIndex(0);
    }
  }, [detectFlow]);

  const reset = useCallback(() => {
    setSelections([]);
    setCurrentStepIndex(0);
    setFlowKey('electric_single_cart');
  }, []);

  const isComplete = selections.length === activeSteps.length;

  const getSelectionForStep = useCallback((stepKey: string) => {
    return selections.find((s) => s.step.step_key === stepKey) || null;
  }, [selections]);

  return {
    allSteps,
    setAllSteps,
    activeSteps,
    currentStep,
    currentStepIndex,
    selections,
    totalPrice,
    flowKey,
    isComplete,
    selectOption,
    goNext,
    goBack,
    goToStep,
    reset,
    restoreSelections,
    getSelectionForStep,
    setSelections,
    setCurrentStepIndex,
    setFlowKey,
  };
}
